import * as THREE from "three";

import React, { Component } from "react";

function animate() {
  requestAnimationFrame(animate);
}

interface LoginBackgroundProps {
  renderSpeed: number;
  sensibility: number;
}

interface LoginBackgroundState {
  loading: boolean;
}

class LoginBackground extends Component<
  LoginBackgroundProps,
  LoginBackgroundState
> {
  private intervalID: NodeJS.Timeout = setInterval(() => {}, 10000);
  private mouseX = 0;
  private mouseY = 0;

  constructor(props: LoginBackgroundProps | Readonly<LoginBackgroundProps>) {
    super(props);
    this.state = {
      loading: false,
    };
  }

  componentWillUnmount() {
    clearInterval(this.intervalID);
  }

  componentDidUpdate(
    prevProps: Readonly<LoginBackgroundProps>,
    prevState: Readonly<{}>,
    snapshot?: any
  ) {
    this.mouseX = 0;
    this.mouseY = -1000;
    if (this.state.loading === false) {
      this.setState({ loading: true });
      setTimeout(() => {
        this.setState({ loading: false });
      }, 2000);
    }
  }

  componentDidMount() {
    const SEPARATION = 120,
      AMOUNTX = 120,
      AMOUNTY = 120;
    let container;
    let camera: THREE.PerspectiveCamera ,
      scene: THREE.Scene,
      renderer: THREE.WebGLRenderer;

    let particles: THREE.Points,
      count = 0;

    let windowHalfX = window.innerWidth / 2;
    let windowHalfY = window.innerHeight / 2;

    let waveScale = 50;
    let scaleScale = 10;

    const far = 8000;

    container = document.createElement("div");
    var parent = document.getElementById("LoginBackground");
    if (parent) {
      parent.appendChild(container);
    }

    camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      1,
      far
    );
    camera.position.z = 500;
    camera.position.y = 5000;

    scene = new THREE.Scene();
    const fog: THREE.Fog = new THREE.Fog (0x020F1D, 1000, 8000);

    const numParticles = AMOUNTX * AMOUNTY;

    const positions = new Float32Array(numParticles * 3);
    const scales = new Float32Array(numParticles);

    let i = 0,
      j = 0;

    for (let ix = 0; ix < AMOUNTX; ix++) {
      for (let iy = 0; iy < AMOUNTY; iy++) {
        positions[i] = ix * SEPARATION - (AMOUNTX * SEPARATION) / 2; // x
        positions[i + 1] = 0; // y
        positions[i + 2] = iy * SEPARATION - (AMOUNTY * SEPARATION) / 2; // z

        scales[j] = 1;

        i += 3;
        j++;
      }
    }

    const geometry = new THREE.PlaneGeometry(
      AMOUNTX * SEPARATION,
      AMOUNTY * SEPARATION,
      AMOUNTX - 1,
      AMOUNTY - 1
    );
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("scale", new THREE.BufferAttribute(scales, 1));

    const material = new THREE.ShaderMaterial({
      fog: true,
      uniforms: {
        color: { value: new THREE.Color(0x97afc3) },
        fogColor:    {value: fog.color },
        fogNear:     {value: fog.near },
        fogFar:      {value: fog.far }
      },
      vertexShader: `attribute float scale;
                void main() {
                    vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
                    gl_PointSize = scale * ( 300.0 / - mvPosition.z );
                    gl_Position = projectionMatrix * mvPosition;
                }`,
      fragmentShader: `
                uniform vec3 color;
                uniform vec3 fogColor;
                uniform float fogNear;
                uniform float fogFar;
                void main() {
                    if ( length( gl_PointCoord - vec2( 0.5, 0.5 ) ) > 0.475 ) discard;
                    gl_FragColor = vec4( color, 1 );
                    #ifdef USE_FOG
                        #ifdef USE_LOGDEPTHBUF_EXT
                            float depth = gl_FragDepthEXT / gl_FragCoord.w;
                        #else
                            float depth = gl_FragCoord.z / gl_FragCoord.w;
                        #endif
                        float fogFactor = smoothstep( fogNear, fogFar, depth );
                        gl_FragColor.rgb = mix( gl_FragColor.rgb, fogColor, fogFactor );
                    #endif
                }
            `,
    });

    //

    particles = new THREE.Points(geometry, material);
    scene.add(particles);

    //

    const lineMat = new THREE.MeshBasicMaterial({
      wireframe: true,
      color: 0x5b7084,
      transparent: true,
      opacity: 0.15,
    });

    const plane = new THREE.Mesh(geometry, lineMat);
    scene.add(plane);

    //

    renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(window.devicePixelRatio < 1 ? 1 : window.devicePixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x020f1d, 1);
    renderer.domElement.setAttribute("style", "position:fixed;z-index:1");
    container.appendChild(renderer.domElement);

    container.style.touchAction = "none";
    window.addEventListener("pointermove", (event) => {
      if (event.isPrimary === false) return;
      this.mouseX = event.clientX - windowHalfX;
      this.mouseY = event.clientY - windowHalfY;
    });

    //

    window.addEventListener("resize", (event) => {
      windowHalfX = window.innerWidth / 2;
      windowHalfY = window.innerHeight / 2;

      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();

      renderer.setSize(window.innerWidth, window.innerHeight);
    });
    requestAnimationFrame(animate);

    this.intervalID = setInterval(() => {
      if (!this.state.loading) {
        camera.position.x += (this.mouseX * 0.3 - camera.position.x) * 0.01;
        camera.position.y +=
          (-this.mouseY * 0.3 - camera.position.y + 300) * 0.01;
        camera.lookAt(scene.position);

        i = 0;
        j = 0;

        for (let ix = 0; ix < AMOUNTX; ix++) {
          for (let iy = 0; iy < AMOUNTY; iy++) {
            positions[i + 1] =
              Math.sin((ix + count) * 0.3) * waveScale +
              Math.sin((iy + count) * 0.5) * waveScale;

            scales[j] =
              (Math.sin((ix + count) * 0.3) + 1) * scaleScale +
              (Math.sin((iy + count) * 0.5) + 1) * scaleScale;

            i += 3;
            j++;
          }
        }

        particles.geometry.attributes.position.needsUpdate = true;
        particles.geometry.attributes.scale.needsUpdate = true;

        renderer.render(scene, camera);

        count += this.props.sensibility;
      } else {
        camera.position.x += (0 * 1.5 - camera.position.x) * 0.01;
        camera.position.y += (1000 * 1.5 - camera.position.y + 300) * 0.01;
        camera.lookAt(scene.position);

        i = 0;
        j = 0;

        for (let ix = 0; ix < AMOUNTX; ix++) {
          for (let iy = 0; iy < AMOUNTY; iy++) {
            positions[i + 1] =
              Math.sin((ix + count) * 0.3) * waveScale +
              Math.sin((iy + count) * 0.5) * waveScale;

            scales[j] =
              (Math.sin((ix + count) * 0.3) + 1) * scaleScale +
              (Math.sin((iy + count) * 0.5) + 1) * scaleScale;

            i += 3;
            j++;
          }
        }

        particles.geometry.attributes.position.needsUpdate = true;
        particles.geometry.attributes.scale.needsUpdate = true;

        renderer.render(scene, camera);

        count += this.props.sensibility;
      }
    }, this.props.renderSpeed);
  }

  render() {
    return <div id="LoginBackground" style={{zIndex: 0, position: "fixed"}}></div>;
  }
}

export default LoginBackground;
